// AdminClientDetail.js — Detalhe do cliente com histórico financeiro de rates
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Loader2,
  TrendingUp,
  Calendar,
  ExternalLink,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v)
    : "—";
const fmtPct = (v) => (v != null ? `${Number(v).toFixed(1)}%` : "—");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

const REGIME_LABELS = {
  contract: "Contrato",
  green_receipts: "Recibos Verdes",
  b2b: "B2B",
};
const BIZ_LABELS = { outsourcing: "Outsourcing", perm: "Perm" };

const AdminClientDetail = () => {
  const { id } = useParams();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("margin_pct"); // margin_pct | margin_amount | rate_hourly

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/clients/${id}/history`, { headers: getAuthHeaders() });
        setData(r.data);
      } catch {
        toast.error("Erro a carregar histórico");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, getAuthHeaders]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans">
        <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
      </div>
    );
  }
  if (!data) return null;

  const { client, stats, items } = data;

  return (
    <div className="min-h-screen bg-slate-50 font-sans" data-testid="admin-client-detail">
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin/clients">
              <Button variant="outline" size="sm" className="border-slate-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-brand-600 flex items-center gap-2">
                <Building2 className="w-6 h-6" />
                {client.name}
                {!client.active && (
                  <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">
                    inativo
                  </span>
                )}
              </h1>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                <span>
                  Prazo: <strong className="font-mono text-slate-700">{client.payment_terms}d</strong>
                </span>
                <span>
                  Meses faturáveis:{" "}
                  <strong className="font-mono text-slate-700">{client.billable_months}</strong>
                </span>
                {client.notes && <span className="italic">· {client.notes}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase text-slate-500 font-semibold">Simulações</div>
              <div className="text-2xl font-bold text-brand-600 mt-1">
                {stats.simulations_count}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase text-slate-500 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Margem média
              </div>
              <div className="text-2xl font-bold text-brand-600 mt-1">
                {fmtPct(stats.avg_margin_percentage)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Evolução da Margem */}
        {items.length > 0 && (() => {
          // Construir série cronológica (oldest → newest)
          const chart = [...items]
            .reverse()
            .map((it) => ({
              date: it.updated_at ? new Date(it.updated_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" }) : "—",
              fullDate: it.updated_at || "",
              candidate: it.candidate_name,
              margin_pct: Number(it.margin_percentage) || 0,
              margin_amount: Number(it.margin_amount) || 0,
              rate_hourly: Number(it.rate_hourly) || 0,
            }));
          // Configuração por métrica
          const METRICS = {
            margin_pct: {
              label: "Margem %",
              title: "Evolução da Margem (%)",
              fmtValue: (v) => `${Number(v).toFixed(1)}%`,
              fmtAxis: (v) => `${v}%`,
              avg: stats.avg_margin_percentage || 0,
              avgLabel: (v) => `Média ${Number(v).toFixed(1)}%`,
            },
            margin_amount: {
              label: "Margem €",
              title: "Evolução da Margem (€)",
              fmtValue: (v) => fmt(v),
              fmtAxis: (v) => `${Math.round(v)}€`,
              avg: chart.length ? chart.reduce((a, b) => a + b.margin_amount, 0) / chart.length : 0,
              avgLabel: (v) => `Média ${fmt(v)}`,
            },
            rate_hourly: {
              label: "Rate Hora",
              title: "Evolução do Rate/Hora (€)",
              fmtValue: (v) => fmt(v),
              fmtAxis: (v) => `${Math.round(v)}€`,
              avg: chart.length ? chart.reduce((a, b) => a + b.rate_hourly, 0) / chart.length : 0,
              avgLabel: (v) => `Média ${fmt(v)}`,
            },
          };
          const m = METRICS[metric];
          return (
            <Card className="bg-white mb-4" data-testid="margin-evolution-chart">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-brand-600">
                    <TrendingUp className="w-4 h-4" /> {m.title}
                  </CardTitle>
                  <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5" data-testid="metric-switcher">
                    {Object.entries(METRICS).map(([key, cfg]) => (
                      <button
                        key={key}
                        onClick={() => setMetric(key)}
                        className={`text-[11px] px-2.5 py-1 rounded transition-colors ${
                          metric === key
                            ? "bg-brand-600 text-white font-semibold"
                            : "text-slate-600 hover:text-brand-600"
                        }`}
                        data-testid={`metric-${key}`}
                      >
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }} />
                    <YAxis
                      stroke="#64748b"
                      tick={{ fontSize: 11 }}
                      tickFormatter={m.fmtAxis}
                      domain={[0, "auto"]}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                      formatter={(value) => [m.fmtValue(value), m.label]}
                      labelFormatter={(label, payload) => {
                        const p = payload && payload[0];
                        return p?.payload?.candidate ? `${label} · ${p.payload.candidate}` : label;
                      }}
                    />
                    <ReferenceLine
                      y={m.avg}
                      stroke="var(--brand-primary)"
                      strokeDasharray="4 4"
                      label={{ value: m.avgLabel(m.avg), fontSize: 10, fill: "var(--brand-primary)", position: "right" }}
                    />
                    <Line
                      type="monotone"
                      dataKey={metric}
                      stroke="var(--brand-primary)"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "var(--brand-primary)" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                {chart.length === 1 && (
                  <p className="text-[10px] text-slate-400 text-center mt-1">
                    Apenas 1 simulação — adicione mais para ver evolução.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* History table */}
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-brand-600">
              <Calendar className="w-4 h-4" /> Histórico de Rates
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                Ainda não há simulações associadas a este cliente.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="history-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                    <tr>
                      <th className="text-left p-2">Data</th>
                      <th className="text-left p-2">Candidato</th>
                      <th className="text-left p-2">Tipo</th>
                      <th className="text-left p-2">Regime</th>
                      <th className="text-right p-2">Bruto</th>
                      <th className="text-right p-2">Rate Mês</th>
                      <th className="text-right p-2">Rate Dia</th>
                      <th className="text-right p-2">Rate Hora</th>
                      <th className="text-right p-2">Margem</th>
                      <th className="text-right p-2">Margem %</th>
                      <th className="text-right p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-2 text-slate-600 whitespace-nowrap">
                          {fmtDate(it.updated_at)}
                        </td>
                        <td className="p-2 font-medium text-slate-800">{it.candidate_name}</td>
                        <td className="p-2">
                          <Badge variant="outline" className="text-[10px] font-normal">
                            {BIZ_LABELS[it.business_type] || it.business_type || "—"}
                          </Badge>
                        </td>
                        <td className="p-2 text-slate-600">
                          {REGIME_LABELS[it.regime_type] || it.regime_type || "—"}
                        </td>
                        <td className="p-2 text-right font-mono">{fmt(it.gross_value)}</td>
                        <td className="p-2 text-right font-mono">{fmt(it.rate_monthly)}</td>
                        <td className="p-2 text-right font-mono">{fmt(it.rate_daily)}</td>
                        <td className="p-2 text-right font-mono">{fmt(it.rate_hourly)}</td>
                        <td className="p-2 text-right font-mono font-semibold text-brand-600">
                          {fmt(it.margin_amount)}
                        </td>
                        <td className="p-2 text-right font-mono">{fmtPct(it.margin_percentage)}</td>
                        <td className="p-2 text-right">
                          <Link to={`/simulator/shared/${it.id}`} target="_blank">
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Abrir simulação">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminClientDetail;
